import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import styles from "./pendingRecordingPopup.module.css";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import {
  useDismissRecordingSessionMutation,
  useFinalizeRecoveredSessionMutation,
  useLazyGetPendingRecordingSessionsQuery,
} from "../../../services/api/user.api";
import { loadAppConfig, resolveApiBaseUrl } from "../../../utils/config";
import { getCookie } from "../../../utils/token";
import { useNavigate } from "react-router-dom";
import { getActiveRecording, isSessionLive, clearActiveRecordingIfSession } from "../../../utils/logoutLock";

interface PendingSession {
  sessionId: string;
  userId: number;
  therapistId?: number;
  patientName: string | null;
  patientGuid: string | null;
  mimeType: string;
  chunkCount: number;
  createdAt: string;
  lastActivityAt: string;
  recordingDurationSeconds: number | null;
  appointmentId: number | null;
  appointmentStartTime: string | null;
  sessionType?: "call" | "recording";
  sharedCallId?: string | null;
  track?: string | null;
}

type DisplayItem =
  | { kind: "recording"; session: PendingSession }
  | { kind: "call"; sharedCallId: string; sessions: PendingSession[] };

interface PendingRecordingPopupProps {
  sessions: PendingSession[];
  onClose: () => void;
  onDismissed: (sessionId: string) => void;
  onFinalized: (sessionId: string, userId: number, recordNumber: number, patientGuid: string | null, patientName: string | null) => void;
  onContinueRecording?: (session: PendingSession) => void;
}

const formatDuration = (seconds: number | null): string => {
  if (!seconds) return "--:--";
  const hh = Math.floor(seconds / 3600);
  const mm = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0");
  const ss = Math.floor(seconds % 60).toString().padStart(2, "0");
  return hh > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`;
};

const formatDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

/** Expanded detail panel for a selected session */
const SessionDetail: React.FC<{
  session: PendingSession;
  onContinue: () => void;
  onProcess: () => void;
  onDismiss: () => void;
  isProcessing: boolean;
  isResumeLoading: boolean;
  error: string | null;
}> = ({ session, onContinue, onProcess, onDismiss, isProcessing, isResumeLoading, error }) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [showConfirmDismiss, setShowConfirmDismiss] = useState(false);
  const audioElRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      if (audioElRef.current) {
        audioElRef.current.pause();
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const loadAudio = useCallback(async () => {
    if (audioUrl) return;
    setAudioLoading(true);
    try {
      const config = await loadAppConfig();
      const baseUrl = resolveApiBaseUrl(config).replace(/\/$/, "");
      const url = `${baseUrl}/MeetingRecords/partialRecording/${session.sessionId}?t=${Date.now()}`;
      const accessToken = getCookie('accessToken');
      const resp = await fetch(url, {
        headers: {
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          'X-App-Name': 'therapist',
        },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      setAudioUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error("[PendingRecording] Failed to load audio:", err);
    } finally {
      setAudioLoading(false);
    }
  }, [session.sessionId, audioUrl]);

  if (isProcessing) {
    return (
      <div className={styles.sessionDetail}>
        <div className={styles.popup__processing}>
          <div className={styles.popup__spinner}></div>
          <p className={styles.popup__processingText}>
            {t("PENDING_RECORDING_PROCESSING") || "Processing..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.sessionDetail}>
      {/* Details grid */}
      <div className={styles.sessionDetail__grid}>
        <div className={styles.sessionDetail__item}>
          <span className={styles.sessionDetail__label}>
            {t("PENDING_RECORDING_DATE") || "Date"}
          </span>
          <span className={styles.sessionDetail__value}>
            {formatDate(session.createdAt)}
          </span>
        </div>
        <div className={styles.sessionDetail__item}>
          <span className={styles.sessionDetail__label}>
            {t("PENDING_RECORDING_DURATION") || "Duration"}
          </span>
          <span className={styles.sessionDetail__value}>
            {formatDuration(session.recordingDurationSeconds)}
          </span>
        </div>
        <div className={styles.sessionDetail__item}>
          <span className={styles.sessionDetail__label}>
            {t("PENDING_RECORDING_CHUNKS") || "Uploaded Parts"}
          </span>
          <span className={styles.sessionDetail__value}>
            {session.chunkCount}
          </span>
        </div>
      </div>

      {/* Audio playback */}
      <div className={styles.sessionDetail__audio}>
        {!audioUrl ? (
          <button
            className={styles.sessionCard__playBtn}
            onClick={loadAudio}
            disabled={audioLoading}
          >
            ▶️ {audioLoading
              ? t("PENDING_RECORDING_LOADING") || "Loading..."
              : t("PENDING_RECORDING_PLAY") || "Play"}
          </button>
        ) : (
          <audio
            ref={audioElRef}
            className={styles.sessionCard__audioPlayer}
            src={audioUrl}
            controls
          />
        )}
      </div>

      {error && <div className={styles.popup__error}>{error}</div>}

      {/* Actions */}
      <div className={styles.sessionCard__actions}>
        <button className={styles.popup__continueBtn} onClick={onContinue} disabled={isResumeLoading}>
          {isResumeLoading
            ? (t("LOADING") || "...")
            : (t("PENDING_RECORDING_RESUME") || "Continue")}
        </button>
        <button className={styles.popup__processBtn} onClick={onProcess}>
          {t("PENDING_RECORDING_PROCESS_NOW") || "Process Now"}
        </button>
        {!showConfirmDismiss ? (
          <button
            className={styles.popup__dismissBtn}
            onClick={() => setShowConfirmDismiss(true)}
          >
            {t("PENDING_RECORDING_DISMISS") || "Dismiss"}
          </button>
        ) : (
          <div className={styles.sessionCard__confirmInline}>
            <span className={styles.sessionCard__confirmText}>
              {t("PENDING_RECORDING_DISMISS_SURE") || "Delete?"}
            </span>
            <button className={styles.popup__confirmDeleteBtn} onClick={onDismiss}>
              {t("PENDING_RECORDING_DISMISS_CONFIRM") || "Yes"}
            </button>
            <button
              className={styles.popup__confirmCancelBtn}
              onClick={() => setShowConfirmDismiss(false)}
            >
              {t("PENDING_RECORDING_DISMISS_CANCEL") || "No"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function buildDisplayItems(sessions: PendingSession[]): DisplayItem[] {
  const items: DisplayItem[] = [];
  const callGroups = new Map<string, PendingSession[]>();

  for (const s of sessions) {
    if (s.sessionType === "call" && s.sharedCallId) {
      const key = String(s.sharedCallId);
      const group = callGroups.get(key) ?? [];
      group.push(s);
      callGroups.set(key, group);
    } else {
      items.push({ kind: "recording", session: s });
    }
  }

  for (const [sharedCallId, group] of callGroups) {
    items.push({ kind: "call", sharedCallId, sessions: group });
  }

  return items;
}

const PendingRecordingPopup: React.FC<PendingRecordingPopupProps> = ({
  sessions,
  onClose,
  onDismissed,
  onFinalized,
  onContinueRecording,
}) => {
  const navigate = useNavigate();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [errorId, setErrorId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Auto-expand if only one session
  const [expandedId, setExpandedId] = useState<string | null>(
    sessions.length === 1 ? sessions[0].sessionId : null
  );

  const [dismissSession] = useDismissRecordingSessionMutation();
  const [finalizeSession] = useFinalizeRecoveredSessionMutation();
  const [triggerRefreshSessions] = useLazyGetPendingRecordingSessionsQuery();
  const toggleExpand = (sessionId: string) => {
    setExpandedId((prev) => (prev === sessionId ? null : sessionId));
  };

  const [activeRecBlockMsg, setActiveRecBlockMsg] = useState<string | null>(null);
  const [resumeLoading, setResumeLoading] = useState(false);

  // ---------------------------------------------------------------------------
  // Reactive cross-tab filter: hide sessions that are live in another tab.
  // The `storage` event fires whenever *another* tab writes to localStorage,
  // so we re-check `isSessionLive` every time the activeRecording entry changes.
  // ---------------------------------------------------------------------------
  const [liveSessionIds, setLiveSessionIds] = useState<Set<string>>(() => {
    const s = new Set<string>();
    sessions.forEach((sess) => {
      if (isSessionLive(sess.sessionId)) s.add(sess.sessionId);
    });
    return s;
  });

  useEffect(() => {
    const recheck = () => {
      const s = new Set<string>();
      sessions.forEach((sess) => {
        if (isSessionLive(sess.sessionId)) s.add(sess.sessionId);
      });
      setLiveSessionIds(s);
    };

    // `storage` event fires only for changes made by OTHER tabs.
    window.addEventListener("storage", recheck);
    return () => window.removeEventListener("storage", recheck);
  }, [sessions]);

  const visibleSessions = sessions.filter(
    (s) => !liveSessionIds.has(s.sessionId)
  );
  const displayItems = useMemo(
    () => buildDisplayItems(visibleSessions),
    [visibleSessions]
  );

  // If every session is now live in another tab, auto-close the popup.
  useEffect(() => {
    if (sessions.length > 0 && visibleSessions.length === 0) {
      onClose();
    }
    if (displayItems.length === 1) {
      const item = displayItems[0];
      setExpandedId(
        item.kind === "call" ? `call-${item.sharedCallId}` : item.session.sessionId
      );
    }
    // Collapse detail panel if it was expanded for a session that went live
    if (expandedId && liveSessionIds.has(expandedId)) {
      setExpandedId(visibleSessions.length === 1 ? visibleSessions[0].sessionId : null);
    }
  }, [displayItems, visibleSessions.length, sessions.length, onClose, liveSessionIds, expandedId]);

  const handleContinue = async (session: PendingSession) => {
    // Guard: block if another recording is already live
    const active = getActiveRecording();
    if (active) {
      setActiveRecBlockMsg(
        `${t("RECORDING_ACTIVE_IN_OTHER_TAB") || "A recording is already active"} (${active.patientName})`
      );
      return;
    }

    // Re-fetch session data from server to get the latest chunkCount & duration
    // (another tab may have resumed and recorded more since this popup was opened)
    setResumeLoading(true);
    let freshSession: PendingSession = session;
    try {
      const result = await triggerRefreshSessions(undefined).unwrap();
      if (result?.success && result.sessions?.length > 0) {
        const found = result.sessions.find((s: any) => s.sessionId === session.sessionId);
        if (found) {
          freshSession = found as PendingSession;
        } else {
          // Session no longer exists on server (dismissed or finalized)
          setActiveRecBlockMsg(
            t("PENDING_RECORDING_SESSION_GONE") || "This recording session is no longer available."
          );
          setResumeLoading(false);
          return;
        }
      }
    } catch {
      // If re-fetch fails, proceed with original data (best-effort)
    }
    setResumeLoading(false);

    if (onContinueRecording) {
      onContinueRecording(freshSession);
    } else {
      navigate("/users/recording", {
        state: {
          Guid: freshSession.patientGuid,
          UsersId: freshSession.userId,
          patientName: freshSession.patientName,
          appointmentId: freshSession.appointmentId ?? undefined,
          appointmentStartTime: freshSession.appointmentStartTime ?? undefined,
          resumeSession: {
            sessionId: freshSession.sessionId,
            chunkCount: freshSession.chunkCount,
            mimeType: freshSession.mimeType,
            durationSeconds: freshSession.recordingDurationSeconds || 0,
          },
        },
      });
    }
    onClose();
  };

  const handleProcess = async (session: PendingSession) => {
    setProcessingId(session.sessionId);
    setErrorId(null);
    setErrorMsg(null);
    if (!session.patientGuid) {
      setErrorId(session.sessionId);
      setErrorMsg(t("PENDING_RECORDING_ERROR") || "Patient GUID is required");
      setProcessingId(null);
      return;
    }
    try {
      const result = await finalizeSession({
        sessionId: session.sessionId,
        userGuid: session.patientGuid,
        totalChunks: session.chunkCount,
        mimeType: session.mimeType,
      }).unwrap();

      if (result.success && result.recordNumber) {
        onFinalized(session.sessionId, session.userId, result.recordNumber, session.patientGuid, session.patientName);
      } else {
        throw new Error(result.message || "Finalization failed");
      }
    } catch {
      setErrorId(session.sessionId);
      setErrorMsg(t("PENDING_RECORDING_ERROR") || "Failed to process recording");
      setProcessingId(null);
    }
  };

  const handleDismiss = async (session: PendingSession) => {
    setProcessingId(session.sessionId);
    setErrorId(null);
    try {
      await dismissSession(session.sessionId).unwrap();
      clearActiveRecordingIfSession(session.sessionId);
      onDismissed(session.sessionId);
    } catch {
      setErrorId(session.sessionId);
      setErrorMsg(t("PENDING_RECORDING_ERROR") || "Failed to dismiss recording");
      setProcessingId(null);
    }
  };

  const handleDismissCallGroup = async (group: PendingSession[]) => {
    const id = group[0]?.sessionId ?? "call";
    setProcessingId(id);
    setErrorId(null);
    try {
      for (const s of group) {
        await dismissSession(s.sessionId).unwrap();
        clearActiveRecordingIfSession(s.sessionId);
        onDismissed(s.sessionId);
      }
    } catch {
      setErrorId(id);
      setErrorMsg(t("PENDING_RECORDING_ERROR") || "Failed to dismiss recording");
      setProcessingId(null);
    }
  };

  return (
    <div className={styles.popup}>
      <span className={styles.popup__icon}>🎙️</span>
      <h2 className={styles.popup__title}>
        {displayItems.length === 1
          ? t("PENDING_RECORDING_TITLE") || "Interrupted Recording Found"
          : `${displayItems.length} ${t("PENDING_RECORDING_TITLE_MULTI") || "Interrupted Recordings Found"}`}
      </h2>
      <p className={styles.popup__description}>
        {t("PENDING_RECORDING_DESCRIPTION_MULTI") ||
          "Previous recording sessions were interrupted. For each session you can listen to the partial recording, continue, process, or dismiss it."}
      </p>

      <div className={styles.sessionListWrapper}>
      <div className={styles.sessionList}>
        {displayItems.map((item) => {
          if (item.kind === "call") {
            const primary = item.sessions[0];
            const maxDuration = Math.max(
              ...item.sessions.map((s) => s.recordingDurationSeconds ?? 0)
            );
            const totalChunks = item.sessions.reduce((n, s) => n + s.chunkCount, 0);
            const rowId = `call-${item.sharedCallId}`;
            return (
              <div key={rowId} className={styles.sessionItem}>
                <button
                  className={`${styles.sessionRow} ${expandedId === rowId ? styles.sessionRowExpanded : ""}`}
                  onClick={() => toggleExpand(rowId)}
                >
                  <span className={styles.sessionRow__icon}>📞</span>
                  <span className={styles.sessionRow__name}>
                    {primary.patientName || "---"}
                  </span>
                  <span className={styles.sessionRow__meta}>
                    {t("PENDING_CALL_RECORDING_INTERRUPTED") || "שיחה הופסקה"} •{" "}
                    {formatDuration(maxDuration || null)} • {totalChunks}{" "}
                    {t("PENDING_RECORDING_CHUNKS") || "חלקים"}
                  </span>
                  <span className={styles.sessionRow__chevron}>
                    {expandedId === rowId ? "▾" : "▸"}
                  </span>
                </button>
                {expandedId === rowId && (
                  <div className={styles.sessionDetail}>
                    <p className={styles.popup__description}>
                      {t("PENDING_CALL_RECORDING_DISMISS_HINT") ||
                        "הקלטת השיחה הופסקה. ניתן למחוק את החלקים שהועלו מהשרת."}
                    </p>
                    {errorId === rowId && errorMsg && (
                      <div className={styles.popup__error}>{errorMsg}</div>
                    )}
                    <div className={styles.sessionCard__actions}>
                      <button
                        className={styles.popup__dismissBtn}
                        onClick={() => handleDismissCallGroup(item.sessions)}
                        disabled={processingId === rowId}
                      >
                        {t("PENDING_RECORDING_DISMISS") || "מחק"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          }

          const session = item.session;
          return (
          <div key={session.sessionId} className={styles.sessionItem}>
            <button
              className={`${styles.sessionRow} ${expandedId === session.sessionId ? styles.sessionRowExpanded : ""}`}
              onClick={() => toggleExpand(session.sessionId)}
            >
              <span className={styles.sessionRow__icon}>👤</span>
              <span className={styles.sessionRow__name}>
                {session.patientName || "---"}
              </span>
              <span className={styles.sessionRow__meta}>
                {formatDate(session.createdAt)} • {formatDuration(session.recordingDurationSeconds)}
              </span>
              <span className={styles.sessionRow__chevron}>
                {expandedId === session.sessionId ? "▾" : "▸"}
              </span>
            </button>

            {expandedId === session.sessionId && (
              <SessionDetail
                session={session}
                onContinue={() => handleContinue(session)}
                onProcess={() => handleProcess(session)}
                onDismiss={() => handleDismiss(session)}
                isProcessing={processingId === session.sessionId}
                isResumeLoading={resumeLoading}
                error={errorId === session.sessionId ? errorMsg : null}
              />
            )}
          </div>
          );
        })}
      </div>
      </div>

      <div className={styles.popup__bottomActions}>
        <button className={styles.popup__closeBtn} onClick={onClose}>
          {t("PENDING_RECORDING_CLOSE") || "Close"}
        </button>
      </div>

      {/* Block message when another recording is live */}
      {activeRecBlockMsg && (
        <div className={styles.popup__activeBlockMessage}>
          <p className={styles.popup__activeBlockText}>{activeRecBlockMsg}</p>
          <button className={styles.popup__closeBtn} onClick={() => setActiveRecBlockMsg(null)}>
            {t("UNDERSTOOD_BUTTON") || "OK"}
          </button>
        </div>
      )}
    </div>
  );
};

export default PendingRecordingPopup;
